import { useState, useEffect, type FormEvent } from 'react';
import type { UserProfile } from '../../../../../shared/types';
import { getCompanyOneTimeJobs, getManagerOneTimeJobs, type OneTimeJobWithId } from '../../../services/oneTimeJob.service';
import { createManualTimeEntry } from '../../../services/timeEntry.service';
import { getErrorMessage } from '@shared/utils/error';
import { BaseModal } from '../../common/BaseModal';
import { ModalButtonGroup } from '../../common/ModalButtonGroup';
import { AlertBox } from '../../common/AlertBox';
import { FormSelectField } from '../../form/FormSelectField';
import { FormTextareaField } from '../../form/FormTextareaField';
import { LABEL_STYLE, INPUT_STYLE } from '../../../styles/modalStyles';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  companyId: string;
  managerId?: string;
  staff: UserProfile[];
  onSuccess: () => void;
  companyTimezone?: string;
}

function toLocalDateTimeValue(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export function CreateManualTimeEntryModal({ isOpen, onClose, companyId, managerId, staff, onSuccess, companyTimezone }: Props) {
  const [jobs, setJobs] = useState<OneTimeJobWithId[]>([]);
  const [loadingJobs, setLoadingJobs] = useState(false);

  const [selectedUserId, setSelectedUserId] = useState('');
  const [selectedJobId, setSelectedJobId] = useState('');
  const [clockIn, setClockIn] = useState('');
  const [clockOut, setClockOut] = useState('');
  const [note, setNote] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!isOpen) return;
    setSelectedUserId('');
    setSelectedJobId('');
    setClockIn('');
    setClockOut('');
    setNote('');
    setError('');

    setLoadingJobs(true);
    const jobsPromise = managerId
      ? getManagerOneTimeJobs(managerId)
      : getCompanyOneTimeJobs(companyId);
    jobsPromise
      .then((data) => {
        setJobs(data.filter((j) => j.status !== 'cancelled'));
      })
      .catch((err) => setError(getErrorMessage(err) || 'Failed to load jobs'))
      .finally(() => setLoadingJobs(false));
  }, [isOpen, companyId, managerId]);

  const activeStaff = staff.filter((u) => u.userType === 'staff' && !u.isArchived);
  const selectedJob = jobs.find((j) => j.id === selectedJobId);

  // Pre-fill times from scheduled job when job is selected
  useEffect(() => {
    if (!selectedJob) return;
    setClockIn(toLocalDateTimeValue(selectedJob.scheduledStart));
    setClockOut(toLocalDateTimeValue(selectedJob.scheduledEnd));
  }, [selectedJobId]);

  const staffOptions = [
    { value: '', label: 'Select staff member…' },
    ...activeStaff.map((u) => ({ value: u.uid, label: `${u.firstName} ${u.lastName}` })),
  ];

  const jobOptions = [
    { value: '', label: loadingJobs ? 'Loading jobs…' : 'Select job…' },
    ...jobs.map((j) => ({
      value: j.id,
      label: `${j.title} — ${j.scheduledStart.toLocaleDateString("en-IE", { ...(companyTimezone && { timeZone: companyTimezone }) })}`,
    })),
  ];

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError('');

    if (!selectedUserId) { setError('Please select a staff member'); return; }
    if (!selectedJobId) { setError('Please select a job'); return; }
    if (!clockIn) { setError('Please enter a clock-in time'); return; }
    if (!clockOut) { setError('Please enter a clock-out time'); return; }
    if (!note.trim()) { setError('Please enter a reason / note for audit trail'); return; }

    const clockInDate = new Date(clockIn);
    const clockOutDate = new Date(clockOut);
    if (clockOutDate <= clockInDate) {
      setError('Clock-out must be after clock-in');
      return;
    }

    setSubmitting(true);
    try {
      await createManualTimeEntry({
        companyId,
        userId: selectedUserId,
        oneTimeJobId: selectedJobId,
        clockIn: clockInDate,
        clockOut: clockOutDate,
        note: note.trim(),
      });
      onSuccess();
      onClose();
    } catch (err) {
      setError(getErrorMessage(err) || 'Failed to create time entry');
    } finally {
      setSubmitting(false);
    }
  };

  const hoursPreview = (() => {
    if (!clockIn || !clockOut) return null;
    const diff = (new Date(clockOut).getTime() - new Date(clockIn).getTime()) / (1000 * 60 * 60);
    if (diff <= 0) return null;
    return diff.toFixed(2);
  })();

  return (
    <BaseModal
      isOpen={isOpen}
      onClose={onClose}
      title="Add Manual Time Entry"
      size="medium"
      closeOnBackdropClick
      loading={submitting}
    >
      <form onSubmit={handleSubmit}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', marginTop: '4px' }}>

          <div style={{
            padding: '12px 14px',
            backgroundColor: '#fffbeb',
            border: '1px solid #fcd34d',
            borderRadius: '8px',
            fontSize: '13px',
            color: '#92400e',
            lineHeight: '1.5',
          }}>
            Use this for jobs where staff worked but never clocked in/out. The entry will be marked as
            <strong> Approved</strong> immediately with your name on record.
          </div>

          {error && <AlertBox type="error" message={error} />}

          <FormSelectField
            label="Staff Member"
            value={selectedUserId}
            onChange={setSelectedUserId}
            options={staffOptions}
            required
          />

          <FormSelectField
            label="Job"
            value={selectedJobId}
            onChange={setSelectedJobId}
            options={jobOptions}
            required
            disabled={loadingJobs}
          />

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
            <div>
              <label style={LABEL_STYLE}>
                Clock In <span style={{ color: '#ef4444' }}>*</span>
              </label>
              <input
                type="datetime-local"
                value={clockIn}
                onChange={(e) => setClockIn(e.target.value)}
                required
                style={INPUT_STYLE}
              />
            </div>
            <div>
              <label style={LABEL_STYLE}>
                Clock Out <span style={{ color: '#ef4444' }}>*</span>
              </label>
              <input
                type="datetime-local"
                value={clockOut}
                onChange={(e) => setClockOut(e.target.value)}
                required
                style={INPUT_STYLE}
              />
            </div>
          </div>

          {hoursPreview && (
            <div style={{ fontSize: '13px', color: '#6b7280' }}>
              Duration: <strong style={{ color: '#111827' }}>{hoursPreview}h</strong>
              {selectedJob && (
                <span style={{ marginLeft: 8 }}>
                  · Hourly rate: <strong style={{ color: '#111827' }}>€{selectedJob.hourlyRate.toFixed(2)}</strong>
                </span>
              )}
            </div>
          )}

          <FormTextareaField
            label="Reason / Note (audit trail)"
            value={note}
            onChange={setNote}
            placeholder="e.g. Staff completed shift but forgot to clock in due to phone issue"
            required
            rows={3}
          />
        </div>

        <ModalButtonGroup
          onCancel={onClose}
          submitLabel={submitting ? 'Creating…' : 'Create Entry'}
          disabled={submitting}
        />
      </form>
    </BaseModal>
  );
}
